import { Text } from '@chakra-ui/react'

import { getCertificationUrl, getCourseUrlBySlug } from '@/client/services/url'

import AuthLink from '@/components/Auth/AuthLink'
import ShwLink from '@/components/ShwLink'

type FaqItem = { question: string; answer: () => JSX.Element }

const baseFaqItems: FaqItem[] = [
  {
    question: 'What is Schoolhouse.world?',
    answer: () => (
      <>
        <Text mb={4}>
          Schoolhouse.world is a <b>free</b> online tutoring platform founded by Sal Khan, the
          founder of Khan Academy. Our mission is to connect the world through learning—our
          community of passionate, dedicated volunteer tutors is here to help. We offer{' '}
          <ShwLink
            href="https://schoolhouse.world/blog/free-online-math-tutoring-sessions/"
            isExternal
          >
            free online math tutoring sessions
          </ShwLink>
          , along with SAT and AP preparation. If you&apos;re interested in joining a session,{' '}
          <AuthLink mode="signup" width="auto">
            sign up
          </AuthLink>{' '}
          for an account to get started.
        </Text>
        <Text>
          Interested in becoming a tutor? The first step is to{' '}
          <ShwLink href={getCertificationUrl('get-certified')}>get certified</ShwLink> in a unit,
          which requires demonstrating your mastery by taking the relevant Khan Academy unit tests.
          After getting certified, you can apply to become a tutor and join our global volunteer
          community.
        </Text>
      </>
    ),
  },
]

const courseSlugToFaqItems: Record<string, FaqItem[]> = {
  algebra: [
    {
      question: 'How much does an algebra tutor cost?',
      answer: () => (
        <>
          <Text mb={4}>
            A local algebra tutor can cost anywhere from $25 to $70 per hour. Online, algebra tutors
            cost anywhere from $0 to $70 per hour.
          </Text>
          <Text>
            At Schoolhouse.world, it’s completely free! You can access small-group algebra tutoring
            sessions anytime, anywhere, run by certified algebra tutors from around the world. Just{' '}
            <AuthLink mode="signup">sign up</AuthLink> for an account to get started.
          </Text>
        </>
      ),
    },
    {
      question: 'Where can I learn algebra online for free?',
      answer: () => (
        <Text>
          There are many ways to learn algebra online–one great place to learn is from the Khan
          Academy <ShwLink href="https://www.khanacademy.org/math/algebra">Algebra 1</ShwLink> and{' '}
          <ShwLink href="https://www.khanacademy.org/math/algebra">Algebra 2</ShwLink>&#32;on your
          own time. Khan Academy is a free, extensive resource for learning. However, if you’re
          looking for an online algebra tutor, sign up for a Schoolhouse.world series or
          session—it&apos;s completely free!
        </Text>
      ),
    },
    {
      question: 'How long does it take to learn Algebra 1?',
      answer: () => (
        <Text>
          Algebra 1 is one of the most foundational subjects in all of mathematics, and it builds
          upon prerequisite skills from pre-algebra. Depending on your comfort with these core
          skills, learning Algebra 1 might take anywhere from a few months to a year.
          Schoolhouse.world also offers{' '}
          <ShwLink href={getCourseUrlBySlug('pre-algebra')}>pre-algebra</ShwLink> sessions if you’d
          like to brush up on the fundamentals.
        </Text>
      ),
    },
    {
      question: 'Do you teach college algebra?',
      answer: () => (
        <Text>
          Yes! The Schoolhouse.world algebra curriculum is aligned with Khan Academy’s{' '}
          <ShwLink href="https://www.khanacademy.org/math/algebra">Algebra 1</ShwLink> and{' '}
          <ShwLink href="https://www.khanacademy.org/math/algebra">Algebra 2</ShwLink> curriculums,
          which cover the same topics as a typical college algebra course.
        </Text>
      ),
    },
    {
      question: 'How does algebra tutoring work on Schoolhouse?',
      answer: () => (
        <Text>
          Schoolhouse.world offers an array of virtual, small-group algebra tutoring sessions. Once
          you <AuthLink mode="signup">create an account</AuthLink> , you can sign up for any session
          you’re interested in for free! When the session is scheduled to start, you’ll receive an
          email reminder to join the session on Zoom. And once you’re done with a session, you can
          follow one of our algebra tutors to receive notifications about upcoming sessions!
        </Text>
      ),
    },
    {
      question: 'Can I learn algebra on my own?',
      answer: () => (
        <>
          <Text mb={4}>
            Once you&apos;ve grasped the fundamental concepts, the best way to learn algebra is by
            practicing. Compared to more advanced subjects like pre-calculus, algebra is a bit
            easier to learn on your own.
          </Text>
          <Text>
            But you can learn anything if you put your mind to it! We believe learning with others
            can always give you a new angle into the subject. At Schoolhouse.world, our tutors are
            here to help give you support and motivation when you need it.
          </Text>
        </>
      ),
    },
    {
      question: 'Is getting math tutoring worth it?',
      answer: () => (
        <>
          <Text mb={4}>
            Math tutoring can be very much worth the extra time, as the interactive discussions help
            you to understand the concepts better and retain the learning for longer.
          </Text>
          <Text>
            If you’re struggling with a concept, or you’d like to review material you’ve already
            learned, tutoring sessions are a great way to get live, personal help. Schoolhouse.world
            offers online algebra tutoring sessions around the clock—we also offer subjects like
            statistics, geometry, and calculus!
          </Text>
        </>
      ),
    },
    {
      question: 'Can I be an algebra tutor on Schoolhouse.world?',
      answer: () => (
        <Text>
          Anyone can become an algebra tutor at Schoolhouse.world! The first step is to{' '}
          <ShwLink href={getCertificationUrl('get-certified')}>get certified</ShwLink> in algebra,
          which requires demonstrating your mastery by taking the relevant Khan Academy Algebra 1
          unit tests. After getting certified, you can apply to become a tutor and join our global
          volunteer community.
        </Text>
      ),
    },
  ],
  statistics: [
    {
      question: 'How do I get help with statistics?',
      answer: () => (
        <Text>
          There are many ways to get statistics help online–for example, Khan Academy is a free,
          extensive resource for learning statistics on your own time. However, if you’re looking
          for live tutoring in statistics, sign up for a Schoolhouse.world series or
          session—it&apos;s completely free!
        </Text>
      ),
    },
    {
      question: 'How does statistics tutoring work on Schoolhouse?',
      answer: () => (
        <Text>
          Schoolhouse.world offers an array of virtual, small-group statistics tutoring sessions.
          Once you <AuthLink mode="signup">create an account</AuthLink>, you can sign up for any
          session you’re interested in for free! When the session is scheduled to start, you’ll
          receive an email reminder to join the session on Zoom. And once you’re done with a
          session, you can follow a statistics tutor to receive notifications about upcoming
          sessions.
        </Text>
      ),
    },
    {
      question: 'How much does statistics tutoring cost?',
      answer: () => (
        <>
          <Text mb={4}>
            A local statistics tutor can cost anywhere from $25 to $70 per hour. Online, statistics
            tutors cost anywhere from $0 to $70 per hour.
          </Text>
          <Text>
            At Schoolhouse.world it’s completely free! You can access small-group statistics
            tutoring sessions anytime, anywhere, run by certified tutors from around the world. Just{' '}
            <AuthLink mode="signup">sign up</AuthLink> for an account to get started.
          </Text>
        </>
      ),
    },
    {
      question: 'Does tutoring help statistics students?',
      answer: () => (
        <>
          <Text mb={4}>
            Math tutoring can be very much worth the extra time, as the interactive discussions help
            you to understand the concepts better and retain the learning for longer.
          </Text>
          <Text>
            If you’re struggling with a concept, or you’d like to review material you’ve already
            learned, Schoolhouse.world is a great way to get statistics help online. We also offer
            sessions in algebra, geometry, and other math subjects!
          </Text>
        </>
      ),
    },
    {
      question: 'Does tutoring improve grades or test scores?',
      answer: () => (
        <Text>
          In studies, tutoring has been shown to boost test scores and improve grades. Our online
          statistics tutors are here to host sessions that will help you reinforce and apply what
          you learn in school, so you can put your best foot forward.
        </Text>
      ),
    },
    {
      question: 'Is statistics harder than calculus?',
      answer: () => (
        <Text>
          Depends on the person! Statistics focuses on understanding data, while calculus focuses on
          functions, limits, and modeling systems.
        </Text>
      ),
    },
    {
      question: 'Can I be a statistics tutor on Schoolhouse.world?',
      answer: () => (
        <Text>
          We’d be excited to have you! Anyone can become an online statistics tutor by signing up
          and <ShwLink href={getCertificationUrl('get-certified')}>getting certified</ShwLink> in a
          subject to prove their mastery and readiness to teach. After getting certified, you can
          apply to become a tutor and join our global volunteer community.
        </Text>
      ),
    },
  ],
  'pre-calculus': [
    {
      question: 'Should I take precalc in high school?',
      answer: () => (
        <Text>
          Pre-calculus is a great way to prepare yourself for calculus while building off prior
          knowledge in geometry and algebra. We&apos;d recommend you take it! And if you&apos;re
          looking for precalc tutoring, Schoolhouse.world is a great resource.
        </Text>
      ),
    },
    {
      question: 'How difficult is precalc?',
      answer: () => (
        <Text>
          High school precalculus covers a wide variety of units that build off algebra and
          geometry, while preparing you for calculus. While it sounds intimidating, it&apos;s
          doable! And if you&apos;re looking for precalculus help, our certified precalculus tutors
          are running sessions around the clock!
        </Text>
      ),
    },
    {
      question: 'How do I improve at precalculus?',
      answer: () => (
        <Text>
          Once you&apos;ve grasped the fundamentals, the best way to improve is to practice. Khan
          Academy has an extensive
          <ShwLink href="https://www.khanacademy.org/math/precalculus">
            precalculus curriculum
          </ShwLink>{' '}
          if you&apos;re looking for practice problems. And if you&apos;re looking for live,
          personal precalculus help, sign up for a session on Schoolhouse.world!
        </Text>
      ),
    },
    {
      question: 'Can you skip precalculus and go to calculus?',
      answer: () => (
        <Text>
          High school precalculus covers topics like trigonometry, functions, and vectors
          that&apos;ll surely come in handy when you&apos;re learning calculus. We wouldn&apos;t
          recommend you skip it, unless you&apos;re really confident in the material!
        </Text>
      ),
    },
    {
      question: 'What’s covered in precalculus?',
      answer: () => (
        <>
          <Text mb={4}>
            Pre-calculus is a broad subject, covering units like complex numbers, vectors, matrices,
            trigonometry, probability, polar equations, rational functions, conic sections, and
            more.
          </Text>
          <Text>
            Our pre-calculus tutors host sessions in these units, and more, around the clock. If you
            can&apos;t find what you&apos;re looking for, you can even request a session and one of
            our precalc tutors will be in touch.
          </Text>
        </>
      ),
    },
    {
      question: 'What are the hardest units in precalculus?',
      answer: () => (
        <Text>
          While it depends on the person, units like polar equations, conic sections, and
          trigonometry are among the harder parts of a traditional pre-calculus course.
        </Text>
      ),
    },
    {
      question: 'Where can I learn precalculus online for free?',
      answer: () => (
        <Text>
          There are many ways to learn precalculus online–Khan Academy is a free, extensive resource
          for learning{' '}
          <ShwLink href="https://www.khanacademy.org/math/precalculus">precalculus</ShwLink>&#32;on
          your own time. However, if you’re looking for an online precalculus tutor, sign up for a
          Schoolhouse.world series or session—it&apos;s completely free!
        </Text>
      ),
    },
    {
      question: 'Is precalculus harder than calculus?',
      answer: () => (
        <>
          <Text mb={4}>
            It really depends on the person! Some students find the fundamentals in pre-calculus
            challenging, like functions and trigonometry, while others find calculus concepts like
            derivatives and integrals harder to grasp.
          </Text>
          <Text>
            Fortunately, Schoolhouse.world doesn&apos;t just offer precalculus tutoring: we have
            certified tutors in all math subjects, ranging from pre-algebra through calculus. Just{' '}
            <AuthLink mode="signup">sign up</AuthLink> for an account to get started!
          </Text>
        </>
      ),
    },
  ],
  trigonometry: [
    {
      question: 'Can you teach yourself trigonometry?',
      answer: () => (
        <>
          <Text mb={4}>
            Once you&apos;ve grasped the concepts, the best way to learn high school trigonometry is
            to practice. The more equations you solve and the more functions you graph, the better
            you&apos;ll understand the fundamentals.
          </Text>
          <Text>
            But you can learn anything if you put your mind to it! We believe learning with others
            can always give you a new angle into the subject. At Schoolhouse.world, our online
            trigonometry tutors are here to help give you support and motivation when you need it.
            Sign up for a trigonometry tutoring session to see what it&apos;s like!
          </Text>
        </>
      ),
    },
    {
      question: 'Where can I learn trigonometry online for free?',
      answer: () => (
        <Text>
          There are many ways to get trigonometry help online–Khan Academy is a free, extensive
          resource for learning{' '}
          <ShwLink href="https://www.khanacademy.org/math/trigonometry">trigonometry</ShwLink>
          &#32;on your own time. However, if you’re looking for an online trig tutor, sign up for a
          Schoolhouse.world series or session—it&apos;s completely free!
        </Text>
      ),
    },
    {
      question: 'Why is trigonometry so hard?',
      answer: () => (
        <>
          <Text mb={4}>
            What makes high school trigonometry hard is what makes it so beautiful—trigonometry
            blurs the lines between algebra and geometry in a way most students might not have seen
            until that point. And once you get a better understanding of trigonometry, you&apos;ll
            start to see how these math concepts show up in the real world!
          </Text>
          <Text>
            If you&apos;re looking for trig tutoring, sign up for a session at
            Schoolhouse.world—it&apos;s completely free! Just{' '}
            <AuthLink mode="signup">sign up</AuthLink> for an account to get started.
          </Text>
        </>
      ),
    },
    {
      question: 'What is the easiest way to learn trigonometry?',
      answer: () => (
        <Text>
          Once you&apos;ve grasped the fundamentals, the best way to improve is to practice. Khan
          Academy has an extensive{' '}
          <ShwLink href="https://www.khanacademy.org/math/trigonometry">
            trigonometry curriculum
          </ShwLink>{' '}
          if you&apos;re looking for practice problems. And if you&apos;d like to, sign up for a
          trigonometry tutoring session on Schoolhouse.world!
        </Text>
      ),
    },
    {
      question: 'How long does it take to learn trigonometry?',
      answer: () => (
        <Text>
          Depending on your comfort with algebra and geometry, learning trigonometry might take
          anywhere from a few months to a year. If you&apos;re having a hard time, one of our online
          trigonometry tutors would be happy to help.
        </Text>
      ),
    },
    {
      question: 'Is trigonometry easier than geometry?',
      answer: () => (
        <Text>
          Depends on the person! If you&apos;re a more visual learner, you might find geometry
          easier to grasp. If you work better with logic and equations, then you might find
          trigonometry easier. In addition to trigonometry tutoring, we also offer{' '}
          <ShwLink href={getCourseUrlBySlug('geometry')}>geometry</ShwLink> sessions!
        </Text>
      ),
    },
  ],
  calculus: [
    {
      question: 'How much does a calculus tutor cost?',
      answer: () => (
        <>
          <Text mb={4}>
            A local calculus tutor can cost anywhere from $25 to $70 per hour. Online, calculus
            tutors cost anywhere from $0 to $70 per hour.
          </Text>
          <Text>
            At Schoolhouse.world, it’s completely free! You can access small-group calculus tutoring
            sessions anytime, anywhere, run by certified calculus tutors from around the world. Just{' '}
            <AuthLink mode="signup">sign up</AuthLink> for an account to get started.
          </Text>
        </>
      ),
    },
    {
      question: 'What is the best way to learn calculus?',
      answer: () => (
        <Text>
          Calculus builds off of many foundational math skills, while introducing plenty of new
          concepts combining theory and application. The best way to reinforce new concepts is
          practice! Khan Academy has an extensive{' '}
          <ShwLink href="https://www.khanacademy.org/math/calculus">calculus</ShwLink> curriculum if
          you’re looking for practice problems. And if you’re looking for live, personal calculus
          tutoring, sign up for a session on Schoolhouse.world!
        </Text>
      ),
    },
    {
      question: 'What can I do to be good at calculus?',
      answer: () => (
        <Text>
          Make sure you’re solid in math leading up to calculus–such as algebra and pre-calculus.
          Then as you’re learning calculus, the best things you can do to improve your skills are to
          practice, identify gaps in your knowledge, and review concepts as many times as you need!
          At Schoolhouse, our online calculus tutors are here to support you to reinforce topics
          until you’re confident in them.
        </Text>
      ),
    },
    {
      question: 'How is calculus different from algebra?',
      answer: () => (
        <Text>
          Algebra lays down the foundations that underlie both pre-calculus and calculus. While
          algebra focuses on functions and equations, calculus focuses on integrals and rates of
          change. In addition to calculus tutoring, we also offer tutoring in{' '}
          <ShwLink href={getCourseUrlBySlug('algebra2')}>algebra</ShwLink> where you can brush up on
          more fundamental concepts.
        </Text>
      ),
    },
    {
      question: 'How can calculus help us?',
      answer: () => (
        <Text>
          Calculus, the culmination of a traditional high school math curriculum, helps us model and
          understand how the world works. These concepts have applications across various
          disciplines, from architecture and engineering to physics and medicine.
        </Text>
      ),
    },
    {
      question: 'Can I tutor calculus?',
      answer: () => (
        <Text>
          Anyone can become an online calculus tutor at Schoolhouse.world! The first step is to get
          certified in calculus, which requires demonstrating your mastery by taking the relevant
          Khan Academy unit tests. After getting certified, you can apply to become a tutor and join
          our global volunteer community.
        </Text>
      ),
    },
  ],
  sat: [
    {
      question: 'Do you offer SAT Reading help?',
      answer: () => (
        <Text>
          Yes! Schoolhouse offers tutoring for both SAT Math and SAT Reading &amp; Writing, tailored
          to the Digital SAT.
        </Text>
      ),
    },
    {
      question: 'Do SAT tutors really help?',
      answer: () => (
        <Text>
          Yes! Receiving support, doing practice problems, and discovering where you can improve can
          greatly improve your SAT score. SAT tutoring is one of several ways to provide this
          structure! For many, meeting with a private SAT tutor or attending SAT prep courses can be
          costly and inaccessible. However, at Schoolhouse all of our online tutoring sessions are
          100% free–we’re committed to broadening access to quality tutoring.
        </Text>
      ),
    },
    {
      question: 'Is Khan Academy good SAT prep?',
      answer: () => (
        <Text>
          Yes! Khan Academy and the College Board have partnered to offer free, personalized{' '}
          <ShwLink href="https://www.khanacademy.org/mission/sat/">SAT practice</ShwLink>. And if
          you’re interested in live, personalized SAT tutoring sessions, check out a free session on
          Schoolhouse.world hosted by our volunteer tutors!
        </Text>
      ),
    },
    {
      question: 'Can you take the SAT without studying?',
      answer: () => (
        <Text>
          It’s definitely possible to take the SAT without studying. However, your SAT score can
          play a role in what colleges you get accepted into, and it never hurts to put your best
          foot forward! Our SAT tutors are here to support you to do your best.
        </Text>
      ),
    },
    {
      question: 'How many hours should I study for the SAT?',
      answer: () => (
        <Text>
          There is no magic number! The time commitment for SAT prep can range from 3 to 20 hours
          per week the months leading up to the test–across practice tests, reading review material,
          attending SAT tutoring sessions, or going to prep classes. This number might depends on
          where you’re at and what your goals are. We’re all for committing yourself to
          practice–just don’t forget to take breaks, drink water, and sleep!
        </Text>
      ),
    },
    {
      question: 'What is the best way to prep for the SAT?',
      answer: () => (
        <Text>
          There are many ways prep for the SAT: private tutoring, self-study, SAT prep courses, and
          more. At Schoolhouse, we offer free, small-group SAT tutoring sessions that anyone,
          anywhere can join. We personally believe that studying is best with the motivation of
          peers and mentors who have your back, and that finances shouldn’t be a barrier to reaching
          your goals.
        </Text>
      ),
    },
  ],
  geometry: [
    {
      question: 'How much does a geometry tutor cost?',
      answer: () => (
        <>
          <Text mb={4}>
            A local geometry tutor can cost anywhere from $25 to $70 per hour. Online, a geometry
            tutor can cost anywhere from $0 to $70 per hour.
          </Text>
          <Text>
            At Schoolhouse.world, it’s completely free! You can access small-group geometry tutoring
            sessions anytime, anywhere, run by certified tutors from around the world. Just{' '}
            <AuthLink mode="signup">sign up</AuthLink> for an account to get started.
          </Text>
        </>
      ),
    },
    {
      question: 'Where can I learn geometry online for free?',
      answer: () => (
        <Text>
          There are many ways to learn geometry online–for example, Khan Academy is a free resource
          for learning <ShwLink href="https://www.khanacademy.org/math/algebra">geometry</ShwLink>{' '}
          on your own time. However, if you’re looking for geometry help, sign up for a Schoolhouse
          tutoring session—it&apos;s completely free!
        </Text>
      ),
    },
    {
      question: 'How does geometry tutoring work on Schoolhouse?',
      answer: () => (
        <Text>
          Schoolhouse.world offers a variety of small-group, virtual geometry tutoring sessions.
          Once you <AuthLink mode="signup">create an account</AuthLink>, you can sign up for any
          session you’re interested in, and get geometry help from a certified tutor—completely
          free! When your session is scheduled to start, you’ll receive an email notification to
          join the session on Zoom. And once you’re done with a session, you can follow one of our
          tutors to receive notifications about upcoming sessions and series!
        </Text>
      ),
    },
    {
      question: 'Can I learn geometry on my own?',
      answer: () => (
        <Text>
          You can learn anything if you put your mind to it! However, we believe learning with
          others can always give you a new angle into the subject. At Schoolhouse.world, our online
          geometry tutors are here to help give you support and motivation when you need it.
        </Text>
      ),
    },
    {
      question: 'Is getting math tutoring worth it?',
      answer: () => (
        <>
          <Text mb={4}>
            Math tutoring can be very much worth the extra time, as the interactive discussions help
            you to understand the concepts better and retain the learning for longer.
          </Text>
          <Text>
            If you’re struggling with a concept, or you’d like to review material you’ve already
            learned, tutoring sessions are a great way to get live, personal help. Schoolhouse.world
            offers online geometry tutoring sessions around the clock—we also offer subjects like
            algebra, statistics, and calculus!
          </Text>
        </>
      ),
    },
    {
      question: 'Can I be a geometry tutor on Schoolhouse.world?',
      answer: () => (
        <Text>
          Anyone can become a geometry tutor at Schoolhouse.world! The first step is to{' '}
          <ShwLink href={getCertificationUrl('get-certified')}>get certified</ShwLink> in geometry,
          which requires demonstrating your mastery by taking the relevant Khan Academy unit tests.
          After getting certified, you can apply to become a tutor and join our global volunteer
          community.
        </Text>
      ),
    },
  ],
  algebra2: [
    {
      question: 'What is taught in Algebra 2?',
      answer: () => (
        <Text>
          Algebra 2 covers topics such as logarithms, polynomials, and complex numbers. These topics
          build on top of fundamental concepts taught in Algebra 1, as well as geometry! On
          Schoolhouse.world, you can find an Algebra 2 tutor online who can provide practice and
          support in the specific Algebra 2 topics you&apos;re having trouble with.
        </Text>
      ),
    },
    {
      question: 'How much does an Algebra 2 tutor cost?',
      answer: () => (
        <Text>
          A local geometry tutor can cost anywhere from $25 to $70 per hour. Online, geometry tutors
          cost anywhere from $0 to $70 per hour. At Schoolhouse, our tutoring sessions are
          completely free! Join small-group tutoring sessions anytime, anywhere, run by certified
          Algebra 2 tutors who’ve mastered the material. Just{' '}
          <AuthLink mode="signup">sign up</AuthLink> for an account to get started with Algebra 2
          tutoring.
        </Text>
      ),
    },
    {
      question: 'How long does it take to learn Algebra 2?',
      answer: () => (
        <Text>
          Algebra 2 is a core high school math subject that builds upon prerequisite skills for
          pre-algebra, Algebra 1, and geometry. Depending on how familiar you are with these
          prerequisites, learning Algebra 2 could take anywhere from several months to a full year.
          At Schoolhouse, find Algebra 2 tutors online who are here to provide guidance to you
          through the school year and beyond.
        </Text>
      ),
    },
    {
      question: 'Do you teach college algebra?',
      answer: () => (
        <Text>
          Yes! The Schoolhouse.world algebra curriculum is aligned with Khan Academy’s{' '}
          <ShwLink href="https://www.khanacademy.org/math/algebra">Algebra 1</ShwLink> and{' '}
          <ShwLink href="https://www.khanacademy.org/math/algebra">Algebra 2</ShwLink> curriculums,
          which cover the same topics as a typical college algebra course.
        </Text>
      ),
    },
    {
      question: 'How does Algebra 2 tutoring work on Schoolhouse.world?',
      answer: () => (
        <Text>
          At Schoolhouse, we offer an assortment of sessions hosted by Algebra 2 tutors online.
          After <AuthLink mode="signup">creating an account</AuthLink>, sign up for a tutoring
          session that fits your schedule–all for free! When the session is about to begin, you’ll
          receive an email remainder to join the session over Zoom. After your session finishes,
          you’ll be able to follow a tutor or topics to stay in the loop for upcoming sessions.
        </Text>
      ),
    },
    {
      question: 'Can I learn Algebra 2 on my own?',
      answer: () => (
        <Text>
          We believe students can learn anything with the right determination! However, we believe
          learning with peers and mentors can always give you new angles into what you’re studying.
          At Schoolhouse, our Algebra 2 tutors are here to help give you support and motivation to
          thrive as you learn.
        </Text>
      ),
    },
    {
      question: 'Is getting math tutoring worth it?',
      answer: () => (
        <>
          <Text mb={4}>
            If you have trouble with a concept, or if you want to practice topics you’ve already
            seen in school, tutoring sessions are a great way to get live, personalized help.
            Luckily, tutoring on Schoolhouse.world is 100% free!
          </Text>
          <Text>
            Among help in many other math subjects, we offer Algebra 2 help in small-group, online
            sessions that are 100% free to attend.
          </Text>
        </>
      ),
    },
    {
      question: 'Can I be an Algebra 2 tutor on Schoolhouse.world?',
      answer: () => (
        <Text>
          Our philosophy is that anyone can be a great tutor! To become an Algebra 2 tutor online on
          Schoolhouse.world, you’ll need to{' '}
          <ShwLink href={getCertificationUrl('get-certified')}>get certified</ShwLink> in Algebra 2,
          which involves showing your mastery by taking the relevant unit tests on Khan Academy.
          After getting your certification, apply to become a tutor and join our global community of
          volunteers.
        </Text>
      ),
    },
  ],
}

export default function getCourseFaqItems(courseSlug: string | null): FaqItem[] | null {
  if (courseSlug == null) {
    return null
  }
  const items = courseSlugToFaqItems[courseSlug]
  if (items == null) {
    return null
  }
  return baseFaqItems.concat(items)
}
